<template>
  <div class="chart-container" :style="{ height: height }">
    <!-- 动态图例 -->
    <div class="chart-legend" v-if="!loading && !error && allStages.length">
      <span class="legend-item" v-for="(stage, idx) in allStages" :key="stage">
        <span class="legend-color" :style="{ backgroundColor: stageColor(idx) }"></span>
        {{ stage }}舞台
      </span>
    </div>
    <div v-if="loading" class="chart-loading">
      <i class="fas fa-spinner fa-spin"></i>
    </div>
    <div v-else-if="error" class="chart-error">
      <i class="fas fa-exclamation-circle"></i> {{ error }}
    </div>
    <v-chart v-else class="chart" :option="chartOption" autoresize />
  </div>
</template>

<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
} from 'echarts/components'
import VChart from 'vue-echarts'
import { getStageColorByIndex } from '../utils/stageColors'

use([
  CanvasRenderer,
  BarChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
])

export default {
  name: 'StagePeakBarChart',
  components: { VChart },
  props: {
    // 传入当日艺人数据，格式：[{ date, artists: [...] }]
    dataByDate: {
      type: Array,
      required: true,
    },
    // 你需要指定当前要显示哪天的艺人峰值对比
    activeDate: {
      type: String,
      required: true,
    },
    height: {
      type: String,
      default: '400px',
    },
  },
  data() {
    return {
      loading: false,
      error: null,
    }
  },
  computed: {
    // 动态获取当天数据中所有不重复舞台，排序保证颜色稳定
    allStages() {
      const dayData = this.dataByDate.find(d => d.date === this.activeDate)
      if (!dayData || !dayData.artists) return []
      const stages = [...new Set(dayData.artists.map(a => a.stage).filter(Boolean))].sort()
      return stages
    },

    chartOption() {
      const dayData = this.dataByDate.find(d => d.date === this.activeDate)

      if (!dayData || !dayData.artists || dayData.artists.length === 0) {
        return {
          title: { text: '无数据' },
          xAxis: { type: 'category', data: [] },
          yAxis: { type: 'value' },
          series: [],
        }
      }

      const artists = dayData.artists
      const stages = this.allStages

      // 按舞台分组
      const stageMap = {}
      stages.forEach(s => { stageMap[s] = [] })
      artists.forEach(a => {
        if (stageMap[a.stage] !== undefined) stageMap[a.stage].push(a)
      })

      // 按最大长度合并为成对项，生成 xAxis 标签
      const maxLength = Math.max(...stages.map(s => stageMap[s].length), 0)
      const xLabels = []
      const seriesData = {}
      stages.forEach(s => { seriesData[s] = [] })

      for (let i = 0; i < maxLength; i++) {
        const nameParts = stages
          .map(s => stageMap[s][i]?.name)
          .filter(Boolean)
        xLabels.push(nameParts.join(' & '))
        stages.forEach(s => {
          seriesData[s].push(stageMap[s][i] ? (stageMap[s][i].maxCount ?? 0) : null)
        })
      }

      const series = stages.map((stage, idx) => ({
        name: stage + '舞台',
        type: 'bar',
        data: seriesData[stage],
        itemStyle: { color: getStageColorByIndex(idx) },
        barMaxWidth: 40,
      }))

      return {
        title: {
          text: `${this.formatDate(this.activeDate)} 峰值人数变化`,
          left: 'center',
          top: 10,
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            let result = `${params[0].axisValue}<br/>`
            params.forEach(param => {
              if (param.value !== null && param.value !== undefined) {
                result += `${param.marker}${param.seriesName}: ${param.value}<br/>`
              }
            })
            return result
          }
        },
        legend: {
          top: 40,
          data: stages.map(s => s + '舞台'),
        },
        grid: {
          top: 80,
          left: 30,
          right: 30,
          bottom: 140,
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: xLabels,
          axisLabel: {
            interval: 0,
            rotate: 45,
            fontSize: 11,
            formatter: (value) => {
              if (value.length > 8) {
                const parts = value.split(' & ')
                if (parts.length > 1) {
                  return parts.map(part => part.length > 6 ? part.substring(0, 6) + '...' : part).join('\n& ')
                } else {
                  return value.substring(0, 8) + '...'
                }
              }
              return value
            },
            lineHeight: 14,
            margin: 16,
          },
        },
        yAxis: {
          type: 'value',
          name: '峰值人数',
        },
        series,
      }
    },
  },
  methods: {
    stageColor(idx) {
      return getStageColorByIndex(idx)
    },
    formatDate(dateStr) {
      const date = new Date(dateStr)
      return `${date.getMonth() + 1}月${date.getDate()}日`
    },
  },
}
</script>

<style scoped>
.chart-container {
  width: 100%;
  position: relative;
  overflow: hidden;
}

.chart {
  width: 100%;
  height: 100%;
}

.chart-loading,
.chart-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 12px;
  color: rgba(0, 0, 0, 0.2);
}

.chart-legend {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 13px;
  color: #666;
  padding-left: 10px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-color {
  width: 12px;
  height: 12px;
  display: inline-block;
  border-radius: 2px;
  border: 1px solid #ccc;
}
</style>